import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Card, Skeleton, message } from "antd";
import { getInspectionResponseDetail } from "../utils/apiService";
import InspectionFormBuilder from "../components/inspections/InspectionFormBuilder";
import {
  getInspectionDraft,
  deleteInspectionDraft,
} from "../utils/inspectionCacheUtils";

const mergeTemplateMeta = (outer, inner) => {
  if (!inner || typeof inner !== "object") return inner;
  if (!outer || typeof outer !== "object") return inner;

  const meta = {
    inspectionName:
      outer?.inspectionName ||
      outer?.InspectionName ||
      outer?.templateName ||
      outer?.TemplateName ||
      outer?.name ||
      outer?.Name,
    templateName:
      outer?.templateName ||
      outer?.TemplateName ||
      outer?.inspectionName ||
      outer?.InspectionName ||
      outer?.name ||
      outer?.Name,
    name:
      outer?.name ||
      outer?.Name ||
      outer?.inspectionName ||
      outer?.InspectionName ||
      outer?.templateName ||
      outer?.TemplateName,
  };

  const next = { ...inner };
  Object.entries(meta).forEach(([k, v]) => {
    if (!next[k] && v) next[k] = v;
  });
  return next;
};

const coerceJson = (val) => {
  if (!val) return null;
  // Prefer nested JSON if present (server returns inspectionItemsJson as string)
  if (val && typeof val === "object" && "inspectionItemsJson" in val) {
    const inner = coerceJson(val.inspectionItemsJson);
    return mergeTemplateMeta(val, inner);
  }
  if (typeof val === "string") {
    try {
      return JSON.parse(val);
    } catch {
      return null;
    }
  }
  if (val && (val.templates || val.schemaVersion)) return val;
  return null;
};

const coerceCompleted = (v) => {
  if (v === true) return true;
  if (v === 1) return true;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (s === "1" || s === "true" || s === "yes") return true;
  }
  return false;
};

const isServerCompleted = (data) => {
  return (
    coerceCompleted(data?.isCompleted) ||
    coerceCompleted(data?.IsCompleted) ||
    coerceCompleted(data?.inspectionResponse?.isCompleted) ||
    coerceCompleted(data?.inspectionResponse?.IsCompleted) ||
    coerceCompleted(data?.data?.isCompleted) ||
    coerceCompleted(data?.data?.IsCompleted)
  );
};

const InspectionResponseDetail = () => {
  const { responseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [templateJson, setTemplateJson] = useState(null);
  const [serverImages, setServerImages] = useState([]);
  const [templateId, setTemplateId] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [reference, setReference] = useState(null);
  const [editable] = useState(location?.state?.editable !== false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [completionDetermined, setCompletionDetermined] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const online = typeof navigator === "undefined" ? true : navigator.onLine;
      const localDraftKey = `resp-${responseId}`;

      // Check if we have a local draft with more recent changes
      let localDraft = null;
      try {
        localDraft = await getInspectionDraft(localDraftKey);
      } catch (e) {
        console.warn("Failed to check local draft:", e);
      }

      const hasLocalDraftTemplate = !!localDraft?.data?.templateJson;

      // Prefer local draft even when online (it may contain newer local-only edits)
      if (hasLocalDraftTemplate) {
        setTemplateJson(localDraft.data.templateJson);
        setServerImages([]);
        const draftTemplateId =
          localDraft.templateId || localDraft.data.templateId;
        if (draftTemplateId) setTemplateId(draftTemplateId);
        const draftCompanyId =
          localDraft.companyId ||
          localDraft.data.companyId ||
          localDraft.data.selectedCompany;
        if (draftCompanyId) setCompanyId(draftCompanyId);
      }

      // If offline, use local draft only
      if (!online) {
        if (hasLocalDraftTemplate) return;
        message.error("Offline - cannot load server draft without local cache");
        return;
      }

      // Fetch with images so we can reflect existing uploads in the UI
      const data = await getInspectionResponseDetail(responseId, true, true);
      const completed = isServerCompleted(data);
      setIsCompleted(completed);
      setCompletionDetermined(true);
      const json = coerceJson(data) || data?.inspectionItemsJson || data;
      const normalized = json?.templates
        ? json
        : {
            schemaVersion: json?.schemaVersion ?? 1,
            templates: json?.templates ?? [],
          };

      // Map server images (separate array) back onto items for display counts
      const images = Array.isArray(data?.images) ? data.images : [];
      const byKey = images.reduce((acc, it) => {
        const urls = Array.isArray(it?.imageUrls) ? it.imageUrls : [];
        const b64 = Array.isArray(it?.imagesBase64) ? it.imagesBase64 : [];
        acc[it.fieldKey] = urls.length || b64.length;
        return acc;
      }, {});

      const withImages = {
        ...normalized,
        templates: (normalized.templates || []).map((item) => {
          const count = byKey[item.itemId] || 0;
          const existingIds = Array.isArray(item?.response?.imageIds)
            ? item.response.imageIds
            : [];
          const imageIds = existingIds.length
            ? existingIds
            : count > 0
              ? Array.from({ length: count }, (_, i) => `server-${i + 1}`)
              : [];
          return {
            ...item,
            response: {
              ...item.response,
              imageIds,
            },
          };
        }),
      };

      // Only apply server template if we don't already have a local draft
      if (!hasLocalDraftTemplate || completed) {
        setTemplateJson(withImages);
      }
      setServerImages(images);

      if (completed) {
        try {
          const keysToDelete = new Set(
            [
              localDraftKey,
              localDraft?.cacheKey,
              localDraft?.draftId,
              localDraft?.originalDraftKey,
              localDraft?.data?.originalDraftKey,
            ]
              .filter(Boolean)
              .map(String),
          );

          for (const k of keysToDelete) {
            try {
              await deleteInspectionDraft(k);
            } catch {}
          }
        } catch {}
      }
      // Extract templateId and companyId from the response data
      if (data?.inspectionTemplateId) {
        setTemplateId(data.inspectionTemplateId);
      }
      if (data?.companyId) {
        setCompanyId(data.companyId);

        const generateRefNumber = () => {
          const min = 10000000;
          const max = 99999999;

          const randomNumber =
            Math.floor(Math.random() * (max - min + 1)) + min;

          return `RNR${randomNumber}-I`;
        };
        setReference(RefNumber);
      }
    } catch (e) {
      console.error("Failed to load response", e);
      message.error("Failed to load response");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responseId]);

  return (
    <div style={{ padding: 16 }}>
      {loading || !templateJson || !completionDetermined ? (
        <Skeleton active />
      ) : (
        <Card>
          <InspectionFormBuilder
            templateJson={templateJson}
            setTemplateJson={setTemplateJson}
            templateId={templateId}
            responseId={responseId}
            serverImages={serverImages}
            companyId={companyId}
            reference={reference}
            editable={editable && !isCompleted}
            draftKey={isCompleted ? undefined : `resp-${responseId}`}
            onSaved={(id, { isCompleted }) => {
              const from = location.state?.from;
              const returnPath =
                from === "accidentReports"
                  ? "/accidentReports"
                  : "/inspections";
              if (isCompleted) {
                try {
                  deleteInspectionDraft(`resp-${id || responseId}`).catch(
                    () => {},
                  );
                } catch {}
                message.success("Inspection submitted successfully");
                navigate(returnPath);
              } else if (id) {
                message.success("Draft saved successfully");
              } else {
                message.success("Changes saved");
                navigate(returnPath);
              }
            }}
          />
        </Card>
      )}
    </div>
  );
};

export default InspectionResponseDetail;
